import { Text, Button } from "@telegram-apps/telegram-ui";
import "./Narration.css";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { NarrationProps } from "./narrationTypes";
import { CHARACTERS } from "../../scripts/consts/NARRATIONS";
import { isRightToLeft } from "../../configs/languages";
import React from "react";

// Define sprite dimensions mapping
const SPRITE_DIMENSIONS: Record<
  string,
  { width: number; height: number; spacing?: number }
> = {
  // Hamsters
  suit_hamster: { width: 105, height: 132, spacing: 14.8 },
  fat_hamster: { width: 85, height: 93, spacing: 35 },
  small_hamster1: { width: 87, height: 96 },
  small_hamster2: { width: 105, height: 91, spacing: 16 },
  warrior_hamster: { width: 120, height: 136 },
  lizard_hamster: { width: 120, height: 101 },
  vampire_hamster: { width: 120, height: 113 },
  king_hamster: { width: 120, height: 132 },

  // Desert enemies
  scorpion: { width: 47, height: 48 },
  snake1: { width: 45, height: 42 },
  snake2: { width: 44, height: 38 },
  cactus: { width: 94, height: 100 },
  vulture: { width: 126, height: 50 },
  golem: { width: 94, height: 90 },
  reptile: { width: 60, height: 21 },

  // Other characters
  dude: { width: 32, height: 32 },
  camel: { width: 135, height: 103, spacing: 21 },
};

export const NarrationComponent: React.FC<NarrationProps> = ({
  narrationId,
  narrationEntry,
  onResponseSelected,
  isTransitioning,
}) => {
  // Use the i18n translation function
  const { t, currentLanguage } = useI18n();

  // Detect if current language is RTL
  const isRtl = isRightToLeft(currentLanguage);

  // Get character data
  const { character, text, responses } = narrationEntry;
  const characterData = CHARACTERS[character];

  // Handle response click
  const handleResponseClick = (nextId: string | null) => {
    onResponseSelected(nextId);
  };

  // For sprite frames, we need to add a class to identify the character
  const characterImageClass = `narration-character-${character}`;

  // Effect to handle sprite frame rendering
  React.useEffect(() => {
    if (!characterData) return;

    // Get the character image element
    const imageElement = document.querySelector(
      `.${characterImageClass}`
    ) as HTMLElement;
    if (!imageElement) return;

    // Only process sprite frames
    if (characterData.image.type !== "spriteFrame") return;

    // Clear any existing content
    imageElement.innerHTML = "";

    // Set up container
    imageElement.style.position = "relative";
    imageElement.style.overflow = "hidden";
    imageElement.style.width = "300px";
    imageElement.style.height = "300px";

    // Create a canvas element to render the frame
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Load the spritesheet image
    const img = new Image();
    img.src = `/assets/img/${characterData.image.key}.webp`;

    const handleLoad = () => {
      // Get frame dimensions from our mapping
      const frameWidth =
        SPRITE_DIMENSIONS[characterData.image.key]?.width || 100;
      const frameHeight =
        SPRITE_DIMENSIONS[characterData.image.key]?.height || 100;
      const spacing = SPRITE_DIMENSIONS[characterData.image.key]?.spacing || 0;

      // Get frame index
      const frameIndex =
        characterData.image.type === "spriteFrame"
          ? characterData.image.frame
          : 0;

      // Calculate source rectangle (where to cut from the spritesheet)
      const sourceX = frameIndex * (frameWidth + spacing);
      const sourceY = 0;

      // Set canvas size to frame size
      canvas.width = frameWidth;
      canvas.height = frameHeight;

      // Draw only the specific frame to the canvas
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        frameWidth,
        frameHeight, // Source rectangle
        0,
        0,
        frameWidth,
        frameHeight // Destination rectangle
      );

      // Add the canvas to the container
      canvas.style.width = "auto";
      canvas.style.height = "100%";
      canvas.style.maxWidth = "100%";
      canvas.style.maxHeight = "100%";
      canvas.style.objectFit = "contain";
      canvas.style.display = "block";
      canvas.style.margin = "auto";

      // Center the canvas in the container
      imageElement.style.display = "flex";
      imageElement.style.alignItems = "center";
      imageElement.style.justifyContent = "center";

      imageElement.appendChild(canvas);
    };

    img.onload = handleLoad;

    // Cleanup function
    return () => {
      // Remove the onload handler to prevent memory leaks
      img.onload = null;

      // Remove the canvas if it exists
      if (imageElement) {
        imageElement.innerHTML = "";
      }
    };
  }, [character, characterData, characterImageClass]);

  // If character data is missing, show a placeholder
  if (!characterData) {
    return <div>Character not found: {character}</div>;
  }

  // Add RTL class to container
  const containerClassName = `narration-container ${
    isTransitioning ? "narration-transitioning" : ""
  } ${isRtl ? "rtl" : "ltr"}`;

  return (
    <div className={containerClassName} dir={isRtl ? "rtl" : "ltr"}>
      {/* Character image */}
      <div className="narration-character-container">
        {characterData.image.type === "image" ? (
          <div
            className="narration-character-image"
            style={{
              backgroundImage: `url(/assets/img/NPC/${characterData.image.key}.webp)`,
            }}
          />
        ) : (
          <div
            className={`narration-character-image ${characterImageClass}`}
            style={{
              position: "relative",
              top: "50px",
              zIndex: 1,
            }}
          />
        )}
      </div>

      {/* Dialog box */}
      <div
        className="narration-dialog"
        style={{ position: "relative", zIndex: 2 }}
      >
        {/* Separate header for character name */}
        <div className="narration-character-name">
          {t(characterData.nameKey)}
        </div>

        {/* Text container for dialog content */}
        <div className="narration-text-container">
          <Text className="narration-text">{t(text)}</Text>
        </div>

        <div className="narration-responses-container">
          {responses.map((response, index) => (
            <Button
              key={`${narrationId}-response-${index}`}
              className="narration-response-button"
              stretched
              onClick={() => handleResponseClick(response.nextId)}
            >
              {t(response.text)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
